import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import Iconify from '../iconify';
import { useProject } from '../../hooks/ProjectContext';

// ---------------------------------------------------------------------------
const PANEL_WIDTH = 370;
const PANEL_HEIGHT = 520;

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

function pageKey(pathname) {
  if (pathname.includes('/home')) return 'home';
  if (pathname.includes('/feasibility')) return 'feasibility';
  if (pathname.includes('/baseline')) return 'baseline';
  if (pathname.includes('/monitoring')) return 'monitoring';
  if (pathname.includes('/stratification')) return 'stratification';
  if (pathname.includes('/sampling')) return 'sampling';
  if (pathname.includes('/library')) return 'library';
  if (pathname.includes('/site-visit')) return 'site-visit';
  if (pathname.includes('/capture')) return 'capture';
  if (pathname.includes('/registry')) return 'registry';
  if (pathname.includes('/sop')) return 'sop';
  return '';
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------
function MessageBubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <Box sx={{
      display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start',
      mb: 1, px: 0.5,
    }}>
      <Paper elevation={0} sx={{
        maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2,
        bgcolor: isUser ? 'primary.main' : 'grey.100',
        color: isUser ? 'primary.contrastText' : 'text.primary',
      }}>
        <Typography variant="body2" sx={{
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontSize: '0.82rem', lineHeight: 1.5,
        }}>
          {content}
        </Typography>
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------
export default function ChatWidget({ visualMode, embedded, onClose }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const { selectedProject, selectedSite } = useProject();
  const location = useLocation();
  const page = pageKey(location.pathname);

  // Load suggestions when page changes
  useEffect(() => {
    fetch(`/v1/chat/suggestions?page=${page}`)
      .then(r => r.ok ? r.json() : [])
      .then(setSuggestions)
      .catch(() => setSuggestions([]));
  }, [page]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Send message
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStreaming(true);

    // Add assistant placeholder
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      console.log('[ChatWidget] sending to /v1/chat', { projectCode: selectedProject?.code, page });
      const response = await fetch('/v1/chat', {
        method: 'POST',
        headers: bearerHeaders(),
        body: JSON.stringify({
          message: text.trim(),
          projectCode: selectedProject?.code || null,
          siteCode: selectedSite?.code || null,
          page,
          history: messages.slice(-10),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') break;
          try {
            const { text: chunk, error } = JSON.parse(payload);
            if (error) throw new Error(error);
            if (chunk) {
              setMessages(prev => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.role === 'assistant') {
                  next[next.length - 1] = { ...last, content: last.content + chunk };
                }
                return next;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      console.error('[ChatWidget] error:', e);
      if (e.name !== 'AbortError') {
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant') {
            const errMsg = last.content ? last.content + `\n\n[Error: ${e.message}]` : `Error: ${e.message}`;
            next[next.length - 1] = { ...last, content: errMsg };
          } else {
            next.push({ role: 'assistant', content: `Error: ${e.message}` });
          }
          return next;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [streaming, messages, selectedProject, selectedSite, page]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Only render when embedded in drawer (activated from header icon)
  if (!embedded) return null;

  // Chat panel content
  const chatPanel = (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%', width: '100%',
    }}>
      {/* Header */}
      <Box sx={{
        bgcolor: 'primary.main', color: 'white',
        px: 2, py: 1, display: 'flex', alignItems: 'center',
      }}>
        <Iconify icon="eva:message-circle-fill" width={20} sx={{ mr: 1 }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>TERRRA AI</Typography>
        <IconButton size="small" onClick={() => setMessages([])} sx={{ color: 'white', mr: 0.5 }}
          title="Clear chat">
          <Iconify icon="eva:trash-2-outline" width={16} />
        </IconButton>
        <IconButton size="small" onClick={embedded ? onClose : () => setOpen(false)} sx={{ color: 'white' }}>
          <Iconify icon="eva:close-fill" width={18} />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', py: 1.5 }}>
        {messages.length === 0 && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              I'm your VERRA project assistant. Ask me about methodology, project status, or help drafting PD/MR sections.
            </Typography>
            {suggestions.length > 0 && (
              <Stack spacing={0.75}>
                {suggestions.map((s, i) => (
                  <Chip key={i} label={s.label} variant="outlined" size="small"
                    onClick={() => sendMessage(s.text)}
                    sx={{ cursor: 'pointer', justifyContent: 'flex-start' }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} role={m.role} content={m.content} />
        ))}
        {streaming && messages[messages.length - 1]?.content === '' && (
          <Box sx={{ px: 2 }}>
            <Typography variant="caption" color="text.secondary">Thinking...</Typography>
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 0.5 }}>
        <TextField
          size="small" fullWidth
          placeholder="Ask about your project..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={streaming}
          multiline maxRows={3}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
        />
        <Button
          variant="contained" size="small"
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || streaming}
          sx={{ minWidth: 40, px: 1 }}
        >
          <Iconify icon="eva:paper-plane-fill" width={18} />
        </Button>
      </Box>
    </Box>
  );

  return chatPanel;
}
